import { useEffect, useState } from 'react';
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { apiRequest, loginRequest, msalConfig } from './config/authConfig';
import { setTokenProvider } from './services/statusApi';
import Dashboard from './components/Dashboard';

const msalInstance = new PublicClientApplication(msalConfig);

function AppContent() {
  const { instance, accounts, inProgress } = useMsal();
  const [initialized, setInitialized] = useState(false);
  const [ready, setReady] = useState(false);

  // MSAL must be initialized before any account/token API is used.
  useEffect(() => {
    let cancelled = false;
    instance
      .initialize()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setInitialized(true);
      });
    return () => {
      cancelled = true;
    };
  }, [instance]);

  // Wire the API token provider once we have a signed-in account.
  useEffect(() => {
    if (!initialized) return;
    if (accounts.length > 0) {
      const account = accounts[0];
      setTokenProvider(async () => {
        try {
          const res = await instance.acquireTokenSilent({ ...apiRequest, account });
          return res.accessToken;
        } catch {
          const res = await instance.acquireTokenPopup({ ...apiRequest, account });
          return res.accessToken;
        }
      });
      setReady(true);
    } else if (inProgress === 'none') {
      setReady(true);
    }
  }, [initialized, accounts, inProgress, instance]);

  const handleLogin = () => {
    void instance.loginRedirect(loginRequest);
  };

  const handleLogout = () => {
    void instance.logoutRedirect();
  };

  if (!initialized || !ready) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-navy" />
        <p className="mt-4 text-sm text-slate-500">Loading…</p>
      </div>
    );
  }

  return (
    <>
      <AuthenticatedTemplate>
        <Dashboard onSignOut={handleLogout} userName={accounts[0]?.username} />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div className="flex min-h-screen flex-col items-center justify-center px-6">
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-lg">
            <div className="mb-8 text-center">
              <h1 className="text-2xl font-bold text-navy">BVA Health Dashboard</h1>
              <p className="mt-2 text-sm text-slate-500">
                Internal status of Bank Vault Academy services. Restricted access.
              </p>
            </div>
            <button
              onClick={handleLogin}
              className="w-full rounded-lg bg-navy px-6 py-3 text-lg font-medium text-white shadow-md transition hover:bg-navy/90"
            >
              Sign in with Microsoft
            </button>
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">
            &copy; {new Date().getFullYear()} Bank Vault Academy
          </p>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AppContent />
    </MsalProvider>
  );
}
